import { useMemo } from "react";
import { EditableAccountPatch } from "../draftState";
import { GetRowCapabilities } from "../editorCapabilities";
import {
    NestedAddScope,
    NestedEditorCallbacks,
    NestedEditorTableMeta,
} from "./nestedTableMeta";
import { NestedEditorHost } from "./nestedRowRefusal";

interface UseNestedEditorTableMetaParams {
    onUpdateAccount: (id: string, patch: EditableAccountPatch) => void;
    onRequestRenameAccount: NestedEditorCallbacks["onRequestRenameAccount"];
    onRenameGroup: (id: string, name: string) => void;
    onAddAccount: (scope: NestedAddScope) => void;
    onAddGroup: (scope: NestedAddScope) => void;
    onSetDisabled: (id: string, isDisabled: boolean) => void;
    onDeleteAccount: (id: string) => void;
    onDeleteGroup: (id: string) => void;
    onCancelPendingAdd: (draftId: string) => void;
    onMergeAccount?: (id: string) => void;
    onRemoveOpeningBalance?: (id: string) => void;
    onReassignRole: (id: string) => void;
    getRowCapabilities: GetRowCapabilities;
    getConstituentCapabilities: GetRowCapabilities;
    duplicateCodeIds: Set<string>;
    duplicateGroupNameIds: Set<string>;
    duplicateNameIds: Set<string>;
    displayedEntityIds: number[] | undefined;
    host: NestedEditorHost;
    pendingDraftIds: ReadonlySet<string>;
    isEditingDisabled: boolean;
}

// Assembles the ViewTable meta (cell callbacks + invalid-code / duplicate group
// name styling ids) for the nested editor, memoized so cells don't re-render on
// unrelated changes.
export const useNestedEditorTableMeta = ({
    onUpdateAccount,
    onRequestRenameAccount,
    onRenameGroup,
    onAddAccount,
    onAddGroup,
    onSetDisabled,
    onDeleteAccount,
    onDeleteGroup,
    onCancelPendingAdd,
    onMergeAccount,
    onRemoveOpeningBalance,
    onReassignRole,
    getRowCapabilities,
    getConstituentCapabilities,
    duplicateCodeIds,
    duplicateGroupNameIds,
    duplicateNameIds,
    displayedEntityIds,
    host,
    pendingDraftIds,
    isEditingDisabled,
}: UseNestedEditorTableMetaParams): NestedEditorTableMeta => {
    const callbacks = useMemo<NestedEditorCallbacks>(
        () => ({
            onUpdateAccount,
            onRequestRenameAccount,
            onRenameGroup,
            onAddAccount,
            onAddGroup,
            onSetDisabled,
            onDeleteAccount,
            onDeleteGroup,
            onCancelPendingAdd,
            onMergeAccount,
            onRemoveOpeningBalance,
            onReassignRole,
        }),
        [
            onUpdateAccount,
            onRequestRenameAccount,
            onRenameGroup,
            onAddAccount,
            onAddGroup,
            onSetDisabled,
            onDeleteAccount,
            onDeleteGroup,
            onCancelPendingAdd,
            onMergeAccount,
            onRemoveOpeningBalance,
            onReassignRole,
        ],
    );

    return useMemo<NestedEditorTableMeta>(
        () => ({
            callbacks,
            getRowCapabilities,
            getConstituentCapabilities,
            duplicateCodeIds,
            duplicateGroupNameIds,
            duplicateNameIds,
            displayedEntityIds,
            host,
            pendingDraftIds,
            isEditingDisabled,
        }),
        [
            callbacks,
            getRowCapabilities,
            getConstituentCapabilities,
            duplicateCodeIds,
            duplicateGroupNameIds,
            duplicateNameIds,
            displayedEntityIds,
            host,
            pendingDraftIds,
            isEditingDisabled,
        ],
    );
};
